import React, {
  useState,
  useCallback,
  useMemo,
  useImperativeHandle,
  useRef,
  useEffect,
} from 'react';

import { AppBar, Tabs as TabsUI, Tab } from '@material-ui/core';
import { isUndefined, assign, isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { TabPanel } from './components';
import * as S from './styles';

const Tabs = React.forwardRef((props, ref) => {
  const {
    color,
    indicatorColor,
    textColor,
    tabs,
    getData,
    initials,
    params,
  } = props;

  const formRef = useRef(null);

  const [isTab, setIsTab] = useState(false);
  const [data, setData] = useState(initials);

  const handleChangeTabs = useCallback((e, value) => {
    const allData = formRef.current.getData();

    setIsTab(Number(value));
    setData((state) => assign(state, allData));
  }, []);

  const a11yProps = useCallback(
    (index) => ({
      id: `scrollable-auto-tab-${index}`,
      'aria-controls': `scrollable-auto-tabpanel-${index}`,
    }),
    []
  );

  const handleSubmit = useCallback(
    async (value) => {
      let isError = false;

      const fields = assign(data, value);
      const isValidates = tabs
        .map((tab, key) => ({ ...tab, key }))
        .filter((tab) => !isUndefined(tab.validate));

      const promises = isValidates.map(async ({ validate, key }) => {
        try {
          await Yup.object().shape(validate).validate(fields, {
            abortEarly: false,
          });
        } catch (err) {
          const validationErrors = {};

          if (err instanceof Yup.ValidationError) {
            err.inner.forEach((error) => {
              validationErrors[error.path] = error.message.replace(
                `${error.path} `,
                ''
              );
            });

            if (!isError) {
              setIsTab(key);
              formRef.current.setErrors(validationErrors);
            }
          }

          isError = true;
        }
      });

      await Promise.all(promises);

      if (!isError) {
        getData({ fields, params });
        setData(fields);
      }
    },
    [tabs, data, params]
  );

  const onValidate = useCallback(() => {
    formRef.current.submitForm();
  }, [formRef]);

  const setFieldValue = useCallback(
    (name, value) => {
      formRef.current.setFieldValue(name, value);
    },
    [formRef]
  );

  const renderTab = useMemo(
    () =>
      tabs.map((tab, index) => (
        <Tab key={index} label={tab.label} {...a11yProps(index)} />
      )),
    [tabs, a11yProps]
  );

  const renderTabPanes = useMemo(
    () =>
      tabs.map(({ component: Component }, index) => (
        <TabPanel key={`pabel-${index}`} value={isTab} index={index}>
          <Component formRef={formRef} tab={(value) => setIsTab(value)} />
        </TabPanel>
      )),
    [tabs, isTab]
  );

  useImperativeHandle(ref, () => ({
    onValidate,
    setFieldValue,
  }));

  useEffect(() => {
    setIsTab(0);
  }, []);

  return (
    <S.Wrapper ref={formRef} onSubmit={handleSubmit} initialData={data}>
      <AppBar position="static" color={color}>
        <TabsUI
          value={isTab}
          onChange={handleChangeTabs}
          indicatorColor={indicatorColor}
          textColor={textColor}
          variant="scrollable"
          scrollButtons="auto"
          aria-label="scrollable auto tabs"
        >
          {renderTab}
        </TabsUI>
        {renderTabPanes}
      </AppBar>
    </S.Wrapper>
  );
});

Tabs.propTypes = {
  color: PropTypes.oneOf(['primary', 'secondary', 'default']),
  indicatorColor: PropTypes.oneOf(['primary', 'secondary']),
  textColor: PropTypes.oneOf(['primary', 'secondary', 'default']),
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      component: PropTypes.element.isRequired,
      validate: PropTypes.objectOf(PropTypes.any),
    })
  ).isRequired,
  getData: PropTypes.func,
  initials: PropTypes.objectOf(PropTypes.any),
  params: PropTypes.objectOf(PropTypes.any),
};

Tabs.defaultProps = {
  color: 'primary',
  indicatorColor: 'primary',
  textColor: 'primary',
  getData: () => undefined,
  initials: {},
  params: {},
};

export default Tabs;
